import { useEffect, useRef, useState } from "react";
import {
  alpha,
  Box,
  ButtonBase,
  Chip,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getDayOverview } from "../../pages/trainer/trainerPageData";
import {
  DAY_ORDER,
  DAY_SHORT_LABELS,
  formatDateLabel,
  getSiteAccent,
  SLOT_STATUS_META,
} from "../../pages/trainer/trainerUtils";
import TrainerMonthDetailsCard from "./TrainerMonthDetailsCard";

const SWIPE_THRESHOLD = 50;
const SWIPE_MAX_OFFSET = 80;
const TAP_SUPPRESSION_MS = 300;

// Rubber-band offset: fast response near zero, diminishing at extremes
const rubberBandOffset = (deltaX) => {
  const sign = Math.sign(deltaX);
  const abs = Math.abs(deltaX);
  return sign * SWIPE_MAX_OFFSET * Math.tanh(abs / (SWIPE_MAX_OFFSET * 2.5));
};

export default function TrainerMonthView({
  isMobile,
  monthCells,
  slotsByDate,
  todayValue,
  rangeSummary,
  selectedMonthDateValue,
  selectedMonthSlots,
  selectedMonthDateLabel,
  selectedMonthSiteCount,
  selectedMonthOverview,
  mobileFocusCue,
  mobileMonthDetailsRef,
  onNavigate,
  onSelectMonthDate,
  onOpenSlot,
}) {
  const theme = useTheme();
  const swipeGestureRef = useRef({
    isActive: false,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    lockedAxis: null,
  });
  const suppressTapUntilRef = useRef(0);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [slideInFrom, setSlideInFrom] = useState(null);

  // Entrance animation: render at offset first, then animate to 0
  useEffect(() => {
    if (slideInFrom === null) return;
    let raf1, raf2;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setSlideInFrom(null));
    });
    return () => {
      cancelAnimationFrame(raf1);
      if (raf2) cancelAnimationFrame(raf2);
    };
  }, [slideInFrom]);

  const handleMonthDateClick = (dateValue) => {
    if (isMobile && Date.now() < suppressTapUntilRef.current) {
      return;
    }

    onSelectMonthDate(dateValue);
  };

  const resetSwipeGesture = () => {
    swipeGestureRef.current = {
      isActive: false,
      startX: 0,
      startY: 0,
      lastX: 0,
      lastY: 0,
      lockedAxis: null,
    };
    setSwipeOffset(0);
  };

  const handleTouchStart = (event) => {
    if (!isMobile || event.touches.length !== 1) {
      resetSwipeGesture();
      return;
    }

    const touch = event.touches[0];
    swipeGestureRef.current = {
      isActive: true,
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      lastY: touch.clientY,
      lockedAxis: null,
    };
    setSwipeOffset(0);
  };

  const handleTouchMove = (event) => {
    const gesture = swipeGestureRef.current;
    if (!gesture.isActive || event.touches.length !== 1) return;

    const touch = event.touches[0];
    const deltaX = touch.clientX - gesture.startX;
    const deltaY = touch.clientY - gesture.startY;

    gesture.lastX = touch.clientX;
    gesture.lastY = touch.clientY;

    // Lock axis once finger moves enough — commit to horizontal or vertical
    if (!gesture.lockedAxis) {
      if (Math.abs(deltaX) > 8 || Math.abs(deltaY) > 8) {
        gesture.lockedAxis =
          Math.abs(deltaX) >= Math.abs(deltaY) ? "x" : "y";
      }
      return;
    }

    if (gesture.lockedAxis === "y") {
      if (swipeOffset !== 0) setSwipeOffset(0);
      return;
    }

    if (event.cancelable) event.preventDefault();

    setSwipeOffset(rubberBandOffset(deltaX));
  };

  const handleTouchEnd = (event) => {
    const gesture = swipeGestureRef.current;
    if (!gesture.isActive) return;

    const touch = event.changedTouches?.[0];
    const endX = touch ? touch.clientX : gesture.lastX;
    const deltaX = endX - gesture.startX;

    resetSwipeGesture();

    if (gesture.lockedAxis !== "x" || Math.abs(deltaX) < SWIPE_THRESHOLD) {
      return;
    }

    suppressTapUntilRef.current = Date.now() + TAP_SUPPRESSION_MS;
    const direction = deltaX < 0 ? 1 : -1;
    // New content enters from the direction the user swiped toward
    setSlideInFrom(direction * 60);
    onNavigate(direction);
  };

  if (isMobile) {
    const isEntering = slideInFrom !== null;
    const isSwiping = Math.abs(swipeOffset) > 0;
    const swipeProgress = SWIPE_MAX_OFFSET > 0
      ? Math.min(1, Math.abs(swipeOffset) / SWIPE_MAX_OFFSET)
      : 0;

    const contentTransform = isEntering
      ? `translateX(${slideInFrom}px) scale(0.97)`
      : `translateX(${swipeOffset}px) scale(${1 - swipeProgress * 0.02})`;
    const contentOpacity = isEntering ? 0.15 : 1 - swipeProgress * 0.3;
    const contentTransition = isEntering
      ? "none"
      : isSwiping
        ? "none"
        : "transform 0.38s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.38s ease";

    return (
      <Stack spacing={1.5}>
        <Paper
          variant="outlined"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={resetSwipeGesture}
          sx={{
            p: 1.25,
            borderRadius: 3,
            borderColor: alpha(theme.palette.divider, 0.7),
            touchAction: "pan-y",
            overscrollBehaviorX: "contain",
            overflow: "hidden",
          }}
        >
          <Stack spacing={1.25}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
            >
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Monatskalender
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Tag antippen, um Trainings einzublenden.
                </Typography>
              </Box>
              <Chip
                size="small"
                label={`${rangeSummary.total} Termine`}
                sx={{
                  height: 22,
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                }}
              />
            </Stack>

            <Box
              sx={{
                transform: contentTransform,
                opacity: contentOpacity,
                transition: contentTransition,
              }}
            >
              <WeekdayRow compact />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                gap: 0.5,
                transform: contentTransform,
                opacity: contentOpacity,
                transition: contentTransition,
              }}
            >
              {monthCells.map((cell) => {
                if (!cell.isCurrentMonth) {
                  return <InactiveMobileMonthCell key={cell.key} />;
                }

                const daySlots = slotsByDate.get(cell.date) || [];
                const dayOverview = getDayOverview(daySlots);
                const isToday = cell.date === todayValue;
                const isSelected = cell.date === selectedMonthDateValue;
                const hasSlotsOnDay = daySlots.length > 0;
                const dayTint = getDayTint(dayOverview, theme);

                return (
                  <ButtonBase
                    key={cell.key}
                    onClick={() => handleMonthDateClick(cell.date)}
                    sx={{
                      width: "100%",
                      aspectRatio: "1 / 1",
                      minHeight: 52,
                      borderRadius: 2.75,
                      border: "1px solid",
                      borderColor: isSelected
                        ? "primary.main"
                        : isToday
                        ? alpha(theme.palette.primary.main, 0.32)
                        : hasSlotsOnDay
                        ? alpha(dayTint, 0.22)
                        : "transparent",
                      background: isSelected
                        ? `linear-gradient(160deg, ${alpha(
                            theme.palette.primary.main,
                            0.14,
                          )}, ${alpha(theme.palette.primary.main, 0.04)})`
                        : hasSlotsOnDay
                        ? `linear-gradient(160deg, ${alpha(dayTint, 0.09)}, ${alpha(
                            theme.palette.background.paper,
                            0.95,
                          )})`
                        : alpha(theme.palette.background.default, 0.42),
                      boxShadow: isSelected
                        ? `0 10px 22px ${alpha(theme.palette.primary.main, 0.14)}`
                        : "none",
                      p: 0.45,
                      alignItems: "stretch",
                      justifyContent: "stretch",
                      transition:
                        "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
                      "&:active": { transform: "scale(0.97)" },
                    }}
                  >
                    <Stack
                      justifyContent="space-between"
                      sx={{ width: "100%", height: "100%", minWidth: 0 }}
                    >
                      <Stack
                        direction="row"
                        alignItems="flex-start"
                        justifyContent="space-between"
                        spacing={0.35}
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: isSelected || isToday ? 800 : 700,
                            color:
                              isSelected || isToday ? "primary.main" : "text.primary",
                            fontSize: "0.82rem",
                            lineHeight: 1,
                          }}
                        >
                          {cell.label}
                        </Typography>
                        <Box />
                      </Stack>
                      {hasSlotsOnDay ? (
                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                          <Box
                            sx={{
                              minWidth: 30,
                              height: 18,
                              px: 0.45,
                              borderRadius: 999,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: alpha(dayTint, 0.14),
                              color: dayTint,
                              fontSize: "0.62rem",
                              fontWeight: 800,
                              lineHeight: 1,
                            }}
                          >
                            {`${dayOverview.bookedCount}/${dayOverview.totalCount}`}
                          </Box>
                        </Box>
                      ) : (
                        <Box />
                      )}
                    </Stack>
                  </ButtonBase>
                );
              })}
            </Box>
          </Stack>
        </Paper>

        <TrainerMonthDetailsCard
          selectedMonthDateValue={selectedMonthDateValue}
          selectedMonthDateLabel={selectedMonthDateLabel}
          selectedMonthSlots={selectedMonthSlots}
          selectedMonthSiteCount={selectedMonthSiteCount}
          selectedMonthOverview={selectedMonthOverview}
          mobileFocusCue={mobileFocusCue}
          mobileMonthDetailsRef={mobileMonthDetailsRef}
          onOpenSlot={onOpenSlot}
        />
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: {
          md: "minmax(0, 1.75fr) minmax(320px, 1fr)",
          lg: "minmax(0, 1.9fr) minmax(340px, 1fr)",
        },
        alignItems: "start",
        gap: 2,
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          p: 1.5,
          borderRadius: 3,
          borderColor: alpha(theme.palette.divider, 0.7),
        }}
      >
        <Stack spacing={1.5}>
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1.5}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Monatskalender
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Tag auswählen, rechts Trainings prüfen und danach den passenden
                Slot buchen.
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75}>
              <Chip
                size="small"
                label={`${rangeSummary.total} Termine`}
                sx={{
                  height: 24,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  color: "primary.main",
                }}
              />
              {selectedMonthDateValue && (
                <Chip
                  size="small"
                  label={formatDateLabel(selectedMonthDateValue, {
                    day: "2-digit",
                    month: "2-digit",
                  })}
                  sx={{
                    height: 24,
                    fontSize: "0.72rem",
                    fontWeight: 700,
                    backgroundColor: alpha(theme.palette.text.primary, 0.06),
                  }}
                />
              )}
            </Stack>
          </Stack>

          <WeekdayRow />

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
              gap: 0.75,
            }}
          >
            {monthCells.map((cell) => {
              if (!cell.isCurrentMonth) {
                return <InactiveDesktopMonthCell key={cell.key} />;
              }

              const daySlots = slotsByDate.get(cell.date) || [];
              const dayOverview = getDayOverview(daySlots);
              const isToday = cell.date === todayValue;
              const isSelected = cell.date === selectedMonthDateValue;
              const hasSlotsOnDay = daySlots.length > 0;
              const dayTint = getDayTint(dayOverview, theme);

              return (
                <ButtonBase
                  key={cell.key}
                  onClick={() => handleMonthDateClick(cell.date)}
                  sx={{
                    display: "block",
                    width: "100%",
                    minHeight: 132,
                    borderRadius: 2.5,
                    textAlign: "left",
                    border: "1px solid",
                    borderColor: isSelected
                      ? "primary.main"
                      : isToday
                      ? alpha(theme.palette.primary.main, 0.34)
                      : hasSlotsOnDay
                      ? alpha(dayTint, 0.2)
                      : alpha(theme.palette.divider, 0.75),
                    background: isSelected
                      ? `linear-gradient(160deg, ${alpha(
                          theme.palette.primary.main,
                          0.14,
                        )}, ${alpha(theme.palette.primary.main, 0.03)})`
                      : hasSlotsOnDay
                      ? `linear-gradient(160deg, ${alpha(dayTint, 0.08)}, ${alpha(
                          theme.palette.background.paper,
                          0.98,
                        )})`
                      : alpha(theme.palette.background.default, 0.55),
                    boxShadow: isSelected
                      ? `0 14px 28px ${alpha(theme.palette.primary.main, 0.12)}`
                      : "none",
                    p: 0.9,
                    verticalAlign: "top",
                    transition:
                      "transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease",
                    "&:hover": {
                      transform: "translateY(-1px)",
                      boxShadow: `0 12px 24px ${alpha(
                        theme.palette.text.primary,
                        0.07,
                      )}`,
                    },
                  }}
                >
                  <Stack spacing={0.8} sx={{ minHeight: 114 }}>
                    <Stack
                      direction="row"
                      alignItems="flex-start"
                      justifyContent="space-between"
                      spacing={0.75}
                    >
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            fontWeight: 800,
                            fontSize: "0.86rem",
                            color:
                              isSelected || isToday ? "primary.main" : "text.primary",
                            lineHeight: 1.1,
                          }}
                        >
                          {cell.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.68rem" }}
                        >
                          {formatDateLabel(cell.date, { weekday: "short" })}
                        </Typography>
                      </Box>

                      {hasSlotsOnDay && (
                        <Box
                          sx={{
                            minWidth: 34,
                            height: 20,
                            px: 0.55,
                            borderRadius: 999,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: alpha(dayTint, 0.14),
                            color: dayTint,
                            fontSize: "0.68rem",
                            fontWeight: 800,
                            lineHeight: 1,
                          }}
                        >
                          {dayOverview.totalCount}
                        </Box>
                      )}
                    </Stack>

                    {hasSlotsOnDay ? (
                      <>
                        <Stack direction="row" spacing={0.45} sx={{ flexWrap: "wrap", rowGap: 0.45 }}>
                          {dayOverview.openCount > 0 && (
                            <CompactStatusChip
                              label={`${dayOverview.openCount} offen`}
                              meta={SLOT_STATUS_META.open}
                            />
                          )}
                          {dayOverview.mineCount > 0 && (
                            <CompactStatusChip
                              label={`${dayOverview.mineCount} meine`}
                              meta={SLOT_STATUS_META.mine}
                            />
                          )}
                          {dayOverview.occupiedCount > 0 && (
                            <CompactStatusChip
                              label={`${dayOverview.occupiedCount} besetzt`}
                              meta={SLOT_STATUS_META.occupied}
                            />
                          )}
                        </Stack>

                        <Stack spacing={0.45} sx={{ flex: 1, overflow: "hidden" }}>
                          {daySlots.slice(0, 2).map((slot) => {
                            const meta = SLOT_STATUS_META[slot.status];
                            const accent = slot.siteAccent || getSiteAccent(0);

                            return (
                              <Box
                                key={`${slot.siteId}-${slot.sessionKey}`}
                                sx={{
                                  px: 0.75,
                                  py: 0.5,
                                  borderRadius: 1.6,
                                  borderLeft: `3px solid ${accent.text}`,
                                  backgroundColor: meta.bg,
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    display: "block",
                                    fontWeight: 700,
                                    fontSize: "0.64rem",
                                    lineHeight: 1.25,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {slot.session.start} {slot.session.title || "Training"}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    display: "block",
                                    fontSize: "0.58rem",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {slot.siteName}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Stack>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.64rem", fontWeight: 600 }}
                        >
                          {daySlots.length > 2
                            ? `+${daySlots.length - 2} weitere rechts ansehen`
                            : "Rechts öffnen, um zu buchen"}
                        </Typography>
                      </>
                    ) : (
                      <Box
                        sx={{
                          flex: 1,
                          borderRadius: 2,
                          border: "1px dashed",
                          borderColor: alpha(theme.palette.divider, 0.9),
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography variant="caption" color="text.disabled">
                          Kein Training
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </ButtonBase>
              );
            })}
          </Box>
        </Stack>
      </Paper>

      <TrainerMonthDetailsCard
        desktop
        selectedMonthDateValue={selectedMonthDateValue}
        selectedMonthDateLabel={selectedMonthDateLabel}
        selectedMonthSlots={selectedMonthSlots}
        selectedMonthSiteCount={selectedMonthSiteCount}
        selectedMonthOverview={selectedMonthOverview}
        mobileFocusCue={mobileFocusCue}
        mobileMonthDetailsRef={mobileMonthDetailsRef}
        onOpenSlot={onOpenSlot}
      />
    </Box>
  );
}

function WeekdayRow({ compact = false }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
        gap: compact ? 0.5 : 0.75,
      }}
    >
      {DAY_ORDER.map((day) => (
        <Typography
          key={day}
          variant="caption"
          sx={{
            textAlign: "center",
            fontWeight: 700,
            color: "text.secondary",
            fontSize: compact ? "0.68rem" : undefined,
            py: compact ? 0.35 : 0.5,
          }}
        >
          {DAY_SHORT_LABELS[day]}
        </Typography>
      ))}
    </Box>
  );
}

function InactiveMobileMonthCell() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        aspectRatio: "1 / 1",
        minHeight: 52,
        borderRadius: 2.5,
        position: "relative",
        opacity: 0.45,
        "&::after": {
          content: '""',
          position: "absolute",
          top: "50%",
          left: "50%",
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: alpha(theme.palette.text.disabled, 0.22),
          transform: "translate(-50%, -50%)",
        },
      }}
    />
  );
}

function InactiveDesktopMonthCell() {
  const theme = useTheme();

  return (
    <Box
      sx={{
        minHeight: 132,
        borderRadius: 2.5,
        backgroundColor: alpha(theme.palette.action.hover, 0.32),
      }}
    />
  );
}

function CompactStatusChip({ label, meta }) {
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 18,
        fontSize: "0.58rem",
        fontWeight: 700,
        backgroundColor: meta.bg,
        color: meta.text,
      }}
    />
  );
}

function getDayTint(dayOverview, theme) {
  if (dayOverview.mineCount > 0) return theme.palette.primary.main;
  if (dayOverview.openCount > 0) return SLOT_STATUS_META.open.text;
  if (dayOverview.occupiedCount > 0) return SLOT_STATUS_META.occupied.text;
  return theme.palette.text.disabled;
}
